import { routeSatisfies, onRouteChange } from "./stepConditions/routeMatch";
import { waitForDomText } from "./stepConditions/domTextPresent";
const STORAGE_KEY_PREFIX = "verascend-tour-progress:";
export class TourManager {
  constructor(definition) {
    this.stepIndex = 0;
    this.active = false;
    this.listeners = /* @__PURE__ */ new Set();
    this.cleanupCurrentCondition = null;
    this.stopRouteWatcher = null;
    this.definition = definition;
  }
  start() {
    if (this.hasFinished()) return;
    this.activate(this.loadProgress());
  }
  /**
   * Per Product's 2026-11-02 design decision: navigating off a step's page
   * hides the tour (see TourOverlay's off-script watcher, which calls stop()
   * rather than silently letting the overlay float over the wrong page) and
   * the user has to explicitly restart it rather than have it silently resume
   * when they navigate back. This always restarts from step 0 — Product's
   * wording was "restart," not "resume," and re-walking the whole tour is the
   * simpler, more predictable affordance for a 1-4 day trial. If that reading
   * is wrong and "resume from where they left off" is actually wanted,
   * `start()` already does that (it continues from saved progress) — swap the
   * restart-icon's handler to call start() instead of restart() in that case.
   */
  restart() {
    this.saveProgress(0);
    this.activate(0);
  }
  stop() {
    var _a, _b;
    this.active = false;
    (_a = this.cleanupCurrentCondition) == null ? void 0 : _a.call(this);
    (_b = this.stopRouteWatcher) == null ? void 0 : _b.call(this);
    this.emit();
  }
  skip() {
    this.saveProgress(this.definition.steps.length);
    this.stop();
  }
  advanceManually() {
    this.goToNextStep();
  }
  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }
  getState() {
    var _a;
    return {
      active: this.active,
      stepIndex: this.stepIndex,
      totalSteps: this.definition.steps.length,
      step: this.active ? (_a = this.definition.steps[this.stepIndex]) != null ? _a : null : null
    };
  }
  get currentStep() {
    return this.definition.steps[this.stepIndex];
  }
  activate(stepIndex) {
    this.stepIndex = stepIndex;
    this.active = true;
    this.stopRouteWatcher = onRouteChange(() => this.checkCurrentCondition());
    this.armCurrentStep();
    this.emit();
  }
  armCurrentStep() {
    var _a;
    (_a = this.cleanupCurrentCondition) == null ? void 0 : _a.call(this);
    const step = this.currentStep;
    if (!step) {
      this.stop();
      return;
    }
    switch (step.advanceOn.type) {
      case "manual":
        this.cleanupCurrentCondition = null;
        break;
      case "route":
        this.checkCurrentCondition();
        this.cleanupCurrentCondition = null;
        break;
      case "dom-text-present":
        this.cleanupCurrentCondition = waitForDomText(
          step.advanceOn,
          () => this.goToNextStep(),
          () => {
            console.warn(`[verascend-tour] step "${step.id}" is taking longer than expected to resolve \u2014 still waiting`);
          }
        );
        break;
      default:
        break;
    }
  }
  checkCurrentCondition() {
    const step = this.currentStep;
    if (!step || step.advanceOn.type !== "route") return;
    if (routeSatisfies(step.advanceOn)) {
      this.goToNextStep();
    }
  }
  goToNextStep() {
    var _a;
    (_a = this.cleanupCurrentCondition) == null ? void 0 : _a.call(this);
    this.stepIndex += 1;
    this.saveProgress(this.stepIndex);
    if (this.stepIndex >= this.definition.steps.length) {
      this.stop();
      return;
    }
    this.armCurrentStep();
    this.emit();
  }
  emit() {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }
  storageKey() {
    return `${STORAGE_KEY_PREFIX}${this.definition.tourId}`;
  }
  loadProgress() {
    try {
      const raw = window.localStorage.getItem(this.storageKey());
      return raw ? Number(raw) : 0;
    } catch {
      return 0;
    }
  }
  saveProgress(index) {
    try {
      window.localStorage.setItem(this.storageKey(), String(index));
    } catch {
    }
  }
  hasFinished() {
    return this.loadProgress() >= this.definition.steps.length;
  }
}
